import { formatLeaveDuration, getHalfDaySessionLabel } from '@/utility/utility';
import { Chip } from './Chip';
import { Tone } from '@gokrew/models/ui.types';
import { Meridiem } from '@gokrew/models/leaves';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';
import { WalletDisplayUnit } from '@gokrew/models/leave-settings';
import { calcDuration } from '@/utility/timesheet';
import { differenceInCalendarDays } from 'date-fns';
import { cn } from '@/lib/utils';
import styles from './DateRangeDisplay.module.css';

interface BaseLeaveDisplayProps {
  days: number;

  startDate: string;
  endDate: string;

  startSession: Meridiem | null;
  endSession: Meridiem | null;

  startTime?: string | null;
  endTime?: string | null;
}

export type DateRangeDisplayProps = BaseLeaveDisplayProps;

export function DateDisplay<T extends DateRangeDisplayProps>(
  props: T & {
    locale?: string;
    compact?: boolean;
    weekday?: Intl.DateTimeFormatOptions['weekday'];
    /** Renders as "Fri, 28 September 2026" instead of a separate weekday line. */
    inlineWeekday?: boolean;
    /**
     * Renders the date alone — the hourly time range and the half-day session
     * are left out so a caller can surface them elsewhere.
     */
    dateOnly?: boolean;
    /** Day.js pattern for the date itself, e.g. "D MMM YYYY". */
    dateFormat?: string;
  }
) {
  const {
    locale,
    compact,
    weekday = 'long',
    inlineWeekday,
    dateOnly,
    dateFormat = 'D MMMM YYYY',
    ...rest
  } = props;
  const { t } = useTranslation(TranslationNS.LibsCommon);
  const { dateLine, timeRange, weekDayLine, weekDayDateLine } =
    formatLeaveDuration(rest, {
      weekConfig: { weekday },
      dateConfig: { format: dateFormat, locale },
      getSessionLabel: (session) => getHalfDaySessionLabel(session, t),
      omitSessionLabel: dateOnly,
    });

  return (
    <div
      className={cn(
        styles.primary,
        compact && styles.primaryCompact,
        'text-brand-primary'
      )}
    >
      <div className={cn(styles.title, compact && styles.titleCompact)}>
        {inlineWeekday ? weekDayDateLine : dateLine}
      </div>
      {timeRange && !dateOnly && (
        <div
          className={cn(
            styles.subtitle,
            compact && styles.subtitleCompact,
            'text-brand-primary-hover'
          )}
        >
          {timeRange}
        </div>
      )}

      {!inlineWeekday && (
        <div className="text-brand-primary-hover">({weekDayLine})</div>
      )}
    </div>
  );
}

export function DurationDisplay<T extends DateRangeDisplayProps>(
  props: T & {
    t: (key: string, options?: Record<string, unknown>) => string;
    walletDisplayUnit?: WalletDisplayUnit;
    hoursPerDay?: number;
  }
) {
  const { t, walletDisplayUnit, hoursPerDay = 8, ...rest } = props;
  const { t: tCommon } = useTranslation(TranslationNS.LibsCommon);
  const isHours = walletDisplayUnit === WalletDisplayUnit.HOURS;

  const isHourly = Boolean(rest.startTime && rest.endTime);
  const [sh = 0, sm = 0] = rest.startTime?.split(':').map(Number) ?? [];
  const [eh = 0, em = 0] = rest.endTime?.split(':').map(Number) ?? [];
  const leaveMinutes = isHourly ? eh * 60 + em - (sh * 60 + sm) : 0;

  const displayValue = isHours
    ? isHourly && leaveMinutes > 0
      ? calcDuration(rest.startTime!, rest.endTime!)
      : `${+(rest.days * hoursPerDay).toFixed(2)}h`
    : `${rest.days} ${rest.days === 1 ? t('duration.day') : t('duration.days')}`;

  // For day-based (non-hourly) requests, the server excludes any date matching
  // the company's weekly-holiday/company-holiday settings before persisting
  // `days`, so the charged value can be lower than the raw calendar span.
  // Surface that gap instead of letting it look like a miscalculation.
  const calendarDays = !isHourly
    ? differenceInCalendarDays(
        new Date(rest.endDate),
        new Date(rest.startDate)
      ) +
      1 -
      (rest.startSession === Meridiem.PM ? 0.5 : 0) -
      (rest.endSession === Meridiem.AM ? 0.5 : 0)
    : 0;
  const excludedDays = !isHourly ? calendarDays - rest.days : 0;

  const exclusionTooltipContent =
    !isHourly && excludedDays > 0 ? (
      <div className="space-y-2 text-xs w-52">
        <p className="text-gray-500 leading-snug">
          {tCommon('leave_dates_preview.excluded_days', {
            totalDays: calendarDays,
            excludedDays,
          })}
        </p>
      </div>
    ) : null;

  const tooltipContent =
    isHourly && leaveMinutes > 0 ? (
      <div className="space-y-2 text-xs w-52">
        <p className="text-gray-500 leading-snug">
          {tCommon('leave_dates_preview.hourly_calculation.shift_explanation')}
        </p>
        <div className="border-t border-gray-100 pt-2 space-y-1">
          <div className="flex justify-between">
            <span className="text-gray-500">
              {tCommon('leave_dates_preview.hourly_calculation.start_end')}
            </span>
            <span>
              {rest.startTime} – {rest.endTime}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">
              {tCommon('leave_dates_preview.hourly_calculation.leave_minutes')}
            </span>
            <span>
              {leaveMinutes}{' '}
              {tCommon('leave_dates_preview.hourly_calculation.min')}
            </span>
          </div>
          <div className="border-t border-gray-200 pt-1 flex justify-between font-medium">
            <span>
              {isHours
                ? tCommon('leave_dates_preview.effective_hours')
                : tCommon('leave_dates_preview.effective_days')}
            </span>
            <span>{displayValue}</span>
          </div>
        </div>
      </div>
    ) : null;

  const finalTooltipContent = tooltipContent ?? exclusionTooltipContent;

  if (finalTooltipContent) {
    return (
      <Tooltip>
        <TooltipTrigger>
          <Chip type={Tone.SOFT_BLUE} size="sm" className="whitespace-nowrap">
            {displayValue}
          </Chip>
        </TooltipTrigger>
        <TooltipContent className="bg-white text-gray-800 shadow-md border border-gray-100">
          {finalTooltipContent}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Chip type={Tone.SOFT_BLUE} size="sm" className="whitespace-nowrap">
      {displayValue}
    </Chip>
  );
}
